import { useEffect, useRef, useState, useCallback } from 'react';
import { useFormContext, UseFormReturn } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

interface UseUnsavedChangesWarningOptions {
  enabled?: boolean;
  message?: string;
  formMethods?: UseFormReturn<any>; // Optional form methods to track dirty state
}

export function useUnsavedChangesWarning(options: UseUnsavedChangesWarningOptions = {}) {
  const {
    enabled = true,
    message = 'You have unsaved changes. Are you sure you want to leave?',
    formMethods,
  } = options;

  const formContext = useFormContext();
  const navigate = useNavigate();
  const [manualChanges, setManualChanges] = useState(() => new Set<string>());

  // Track form dirty state with useRef since we might be outside FormProvider context
  const formDirtyRef = useRef(false);

  // Use formMethods if provided, otherwise fall back to formContext
  const activeForm = formMethods || formContext;

  // Safely get isDirty from form context, default to false if context is null
  const isDirty = activeForm?.formState?.isDirty || formDirtyRef.current || false;

  // Update our ref when form context changes
  useEffect(() => {
    if (activeForm?.formState?.isDirty !== undefined) {
      formDirtyRef.current = activeForm.formState.isDirty;
    }
  }, [activeForm?.formState?.isDirty]);

  // Calculate total unsaved changes
  const hasUnsavedChanges = isDirty || manualChanges.size > 0;

  // Intercept all Link clicks globally
  useEffect(() => {
    if (!enabled) return;

    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const link = target.closest('a[href]');

      if (link && hasUnsavedChanges) {
        const href = link.getAttribute('href');

        // Only intercept internal navigation (not external links)
        if (href && (href.startsWith('/') || href.startsWith('#'))) {
          event.preventDefault();
          const confirmed = window.confirm(message);
          if (confirmed) {
            // Use React Router navigation to avoid beforeunload
            navigate(href);
          }
        }
      }
    };

    document.addEventListener('click', handleClick, true);

    return () => {
      document.removeEventListener('click', handleClick, true);
    };
  }, [enabled, message, navigate, hasUnsavedChanges, manualChanges]);

  // Handle browser navigation (back/forward buttons, closing tab, etc.)
  useEffect(() => {
    if (!enabled) return;

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        event.preventDefault();
        event.returnValue = message;
        return message;
      }
    };

    const handlePopState = (event: PopStateEvent) => {
      if (hasUnsavedChanges) {
        event.preventDefault();
        const confirmed = window.confirm(message);
        if (!confirmed) {
          // Push the current state back to prevent navigation
          window.history.pushState(null, '', window.location.pathname + window.location.search);
          return;
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [enabled, message, hasUnsavedChanges]);

  // Function to manually mark a change as unsaved
  const markUnsavedChange = useCallback((key: string) => {
    setManualChanges((prev) => {
      const newSet = new Set(prev);
      newSet.add(key);
      return newSet;
    });
  }, []);

  // Function to manually clear a specific unsaved change
  const clearUnsavedChange = useCallback((key: string) => {
    setManualChanges((prev) => {
      const newSet = new Set(prev);
      newSet.delete(key);
      return newSet;
    });
  }, []);

  // Function to clear all unsaved changes state (call after successful save)
  const clearUnsavedChanges = useCallback(() => {
    setManualChanges(new Set());
    formDirtyRef.current = false;
    activeForm?.reset(activeForm?.getValues());
  }, []);

  return {
    hasUnsavedChanges,
    clearUnsavedChanges,
    markUnsavedChange,
    clearUnsavedChange,
  };
}
